import React from 'react';
import { Grid, Typography, Button } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { useHookstate } from '@hookstate/core';
import type { UserListItem } from '../../interfaces/IWarehouseForm';
import { useLang } from '~/hooks/useLang';
import FilterAutocompleteFetch from '~/components/FilterAutocompleteFetch';
import OperatorsList from '../OperatorsList';

interface AssociateOperatorsProps {
  readonly operatorsList: readonly UserListItem[];
  readonly onAddOperator: (user: any) => boolean;
  readonly onRemoveOperator: (userId: number) => void;
  readonly disabledFields?: boolean;
}

export default function AssociateOperators({ 
  operatorsList, 
  onAddOperator,
  onRemoveOperator,
  disabledFields 
}: AssociateOperatorsProps) {
  const { translate } = useLang();
  const [selectedUserId, setSelectedUserId] = React.useState<number | null>(null);
  const [resetKey, setResetKey] = React.useState(0);
  const fullUserObject = useHookstate<any>(null);


  const handleAddOperator = () => {
    const user = fullUserObject.get();
    if (user) {
      const success = onAddOperator(user);
      if (success) {
        setSelectedUserId(null);
        fullUserObject.set(null);
        setResetKey(prev => prev + 1);
      }
    }
  };

  return (
    <Grid container xs={12} display="flex" flexDirection="column" spacing={2}>
      <Grid item container xs={12} spacing={2}>
        <Grid item xs={12}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            {translate('Select users who will have operation permissions for this warehouse')}
          </Typography>
        </Grid>
        
        <Grid item xs={12} md={2}>
          <FilterAutocompleteFetch
            key={resetKey}
            label={translate('Select User')}
            urlFetch="/user/get"
            onFetchChange={(userId) => setSelectedUserId(userId)}
            disabled={disabledFields}
            size="small"
            dataRequest="user"
            resetValues={resetKey > 0}
            getFullObject={fullUserObject}
          />
        </Grid>
        
        <Grid item xs={12} md={2} display="flex" alignItems="center">
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAddOperator}
            disabled={!selectedUserId || disabledFields}
            fullWidth
            size="small"
            sx={{ height: '40px' }}
          >
            {translate('Add')}
          </Button>
        </Grid>
      </Grid>

      <Grid item container xs={12} spacing={2}>
        <Grid item xs={12}>
          <Typography variant="subtitle1" gutterBottom sx={{ mt: 2 }}>
            {translate('Operators List')}
          </Typography>
        </Grid>
        <OperatorsList 
          operatorsList={operatorsList} 
          onRemove={onRemoveOperator}
        />
      </Grid>
    </Grid>
  );
}
